import { toThunkRejectValue } from "../../../Common/isRequestCanceled";

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  SUPERADMIN_DELETE,
  SUPERADMIN_LIST,
  SUPERADMIN_PERMISSION,
  ADD_SUPERADMIN_PERMISSION,
  SUPERADMIN_ASSIGNED_STORE_LIST,
  BASE_NODE_URL
} from "../../../Constants/Config";

const initialState = {
  loading: false,
  superadminList: [],
  superadminDetail: null, // Single superadmin data for edit
  superadminDetailLoading: false,
  adminPermissions: [], // Array of permission codes (e.g., ["NS", "SNS", "DNS"])
  assignedStores: null,
  permissionsLoading: false,
  // Store list for assignment with pagination
  storeList: [],
  storeListLoading: false,
  storeListLoadingMore: false, // Loading more for infinite scroll
  storeListPage: 0,
  storeListHasMore: true,
  storeListTotal: 0,
  isAllStore: 0,
  // Permission submission
  permissionSubmitLoading: false,
  // Superadmin insert/update
  superadminSubmitLoading: false,
  successMessage: "",
  error: "",
};



export const fetchsuperadminList = createAsyncThunk(
  "Superadmin/fetchsuperadminList",
  async (data, { rejectWithValue }) => {
    // console.log("data",data)
    const { token, ...dataNew } = data;

    try {
      const response = await axios.post(
        BASE_NODE_URL + SUPERADMIN_LIST,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data?.status === true) {
        return response?.data?.results;
      } else {
        console.error("Unexpected response", response);
        return rejectWithValue(response.data || "Unexpected error");
      }
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

export const deletesuperadminData = createAsyncThunk(
  "Superadmin/deletesuperadminData",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + SUPERADMIN_DELETE,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`, // Use data?.token directly
          },
        }
      );
      if (response) {
        return {
          id: data.user_id,
        };
      }
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

// Fetch superadmin permissions from API
export const fetchSuperadminPermissions = createAsyncThunk(
  "Superadmin/fetchSuperadminPermissions",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + SUPERADMIN_PERMISSION,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data?.status === true) {
        // Convert comma-separated permissions string to array
        const permissionsArray = response.data?.permissions
          ? response.data.permissions.split(",").map((p) => p.trim())
          : [];
        return {
          permissions: permissionsArray,
          assignedStores: response.data?.assigned_stores || null,
        };
      } else {
        return rejectWithValue(response.data?.msg || "Failed to fetch permissions");
      }
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const STORE_LIST_LIMIT = 50; // Number of stores to fetch per page

// Fetch assigned store list for superadmin (initial load with pagination)
export const fetchAssignedStoreList = createAsyncThunk(
  "Superadmin/fetchAssignedStoreList",
  async (data, { rejectWithValue }) => {
    const { token, offset = 0, limit = STORE_LIST_LIMIT, ...dataNew } = data;
    try {
      const formData = { ...dataNew, offset, limit };
      const response = await axios.post(
        BASE_NODE_URL + SUPERADMIN_ASSIGNED_STORE_LIST,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data?.status === true) {
        const stores = response.data?.results?.map((store) => ({
          id: store.merchant_id,
          title: store.name || `${store.f_name || ''} ${store.l_name || ''}`.trim() || store.merchant_id,
          merchant_id: store.merchant_id,
          name: store.name,
        })) || [];
        const total = response.data?.total_count ?? stores.length;
        return {
          storeList: stores,
          isAllStore: response.data?.is_all_store ?? 0,
          total,
          hasMore: stores.length === limit,
          offset,
        };
      } else {
        return rejectWithValue(response.data?.msg || "Failed to fetch store list");
      }
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

// Load more stores (for infinite scroll)
export const loadMoreStores = createAsyncThunk(
  "Superadmin/loadMoreStores",
  async (data, { rejectWithValue, getState }) => {
    const { token, ...dataNew } = data;
    const state = getState();
    const currentOffset = state.superadminList.storeList.length;
    const limit = STORE_LIST_LIMIT;

    try {
      const formData = { ...dataNew, offset: currentOffset, limit };
      const response = await axios.post(
        BASE_NODE_URL + SUPERADMIN_ASSIGNED_STORE_LIST,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data?.status === true) {
        const stores = response.data?.results?.map((store) => ({
          id: store.merchant_id,
          title: store.name || `${store.f_name || ''} ${store.l_name || ''}`.trim() || store.merchant_id,
          merchant_id: store.merchant_id,
          name: store.name,
        })) || [];
        const total = response.data?.total_count ?? 0;
        return {
          storeList: stores,
          total,
          hasMore: stores.length === limit,
        };
      } else {
        return rejectWithValue(response.data?.msg || "Failed to load more stores");
      }
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

// Fetch single superadmin by ID for editing
export const fetchSuperadminById = createAsyncThunk(
  "Superadmin/fetchSuperadminById",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + SUPERADMIN_LIST,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data?.status === true && response.data?.results?.length > 0) {
        return response.data.results[0];
      } else {
        return rejectWithValue(response.data?.msg || "Superadmin not found");
      }
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

// Add/Update superadmin permissions
export const addSuperadminPermission = createAsyncThunk(
  "Superadmin/addSuperadminPermission",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + ADD_SUPERADMIN_PERMISSION,
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data?.status === true) {
        return response.data;
      } else {
        return rejectWithValue(response.data?.msg || "Failed to update permissions");
      }
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

// Insert new superadmin
export const insertSuperadmin = createAsyncThunk(
  "Superadmin/insertSuperadmin",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + "superadmin/insert-superadmin",
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data?.status === true) {
        return response.data;
      } else {
        if (response.data?.errors) {
          return rejectWithValue({ errors: response.data.errors });
        }
        return rejectWithValue(response.data?.msg || "Failed to insert superadmin");
      }
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

// Update existing superadmin
export const updateSuperadmin = createAsyncThunk(
  "Superadmin/updateSuperadmin",
  async (data, { rejectWithValue }) => {
    const { token, ...dataNew } = data;
    try {
      const response = await axios.post(
        BASE_NODE_URL + "superadmin/update-superadmin",
        dataNew,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data?.status === true) {
        return response.data;
      } else {
        if (response.data?.errors) {
          return rejectWithValue({ errors: response.data.errors });
        }
        return rejectWithValue(response.data?.msg || "Failed to update superadmin");
      }
    } catch (error) {
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const superadminSlice = createSlice({
  name: "Superadmin",
  initialState,
  reducers: {
    // Action to clear admin permissions (useful on logout)
    clearAdminPermissions: (state) => {
      state.adminPermissions = [];
      state.assignedStores = null;
    },
    // Clear superadmin detail (useful when leaving edit page)
    clearSuperadminDetail: (state) => {
      state.superadminDetail = null;
    },
    // Clear store list
    clearStoreList: (state) => {
      state.storeList = [];
      state.isAllStore = 0;
      state.storeListHasMore = true;
      state.storeListPage = 0;
      state.storeListTotal = 0;
      state.storeListLoadingMore = false;
    },
  },
  extraReducers: (builder) => {
    // fetchsuperadminList
    builder.addCase(fetchsuperadminList.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchsuperadminList.fulfilled, (state, action) => {
      state.loading = false;
      state.superadminList = action.payload;
      state.error = "";
    });
    builder.addCase(fetchsuperadminList.rejected, (state, action) => {
      state.loading = false;
      state.superadminList = [];
      state.error = action?.error?.message || "Unable to fetch data";
    });

    // deletesuperadminData
    builder.addCase(deletesuperadminData.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deletesuperadminData.fulfilled, (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.superadminList = state.superadminList.filter(
        (item) => item && item.id !== action.payload.id
      );
      state.error = "";
    });
    builder.addCase(deletesuperadminData.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error.message;
    });

    // fetchSuperadminPermissions
    builder.addCase(fetchSuperadminPermissions.pending, (state) => {
      state.permissionsLoading = true;
    });
    builder.addCase(fetchSuperadminPermissions.fulfilled, (state, action) => {
      state.permissionsLoading = false;
      state.adminPermissions = action.payload.permissions;
      state.assignedStores = action.payload.assignedStores;
      state.error = "";
    });
    builder.addCase(fetchSuperadminPermissions.rejected, (state, action) => {
      state.permissionsLoading = false;
      state.adminPermissions = [];
      state.assignedStores = null;
      state.error = action.payload?.message || "Failed to fetch permissions";
    });

    // fetchAssignedStoreList (initial load with pagination)
    builder.addCase(fetchAssignedStoreList.pending, (state) => {
      state.storeListLoading = true;
    });
    builder.addCase(fetchAssignedStoreList.fulfilled, (state, action) => {
      state.storeListLoading = false;
      state.storeList = action.payload.storeList;
      state.isAllStore = action.payload.isAllStore;
      state.storeListTotal = action.payload.total;
      state.storeListHasMore = action.payload.hasMore;
      state.storeListPage = 1;
      state.error = "";
    });
    builder.addCase(fetchAssignedStoreList.rejected, (state, action) => {
      state.storeListLoading = false;
      state.storeList = [];
      state.isAllStore = 0;
      state.storeListHasMore = false;
      state.storeListPage = 0;
      state.error = action.payload?.message || "Failed to fetch store list";
    });

    // loadMoreStores (infinite scroll pagination)
    builder.addCase(loadMoreStores.pending, (state) => {
      state.storeListLoadingMore = true;
    });
    builder.addCase(loadMoreStores.fulfilled, (state, action) => {
      state.storeListLoadingMore = false;
      // Append new stores to existing list (avoid duplicates)
      const existingIds = new Set(state.storeList.map(s => s.id));
      const newStores = action.payload.storeList.filter(s => !existingIds.has(s.id));
      state.storeList = [...state.storeList, ...newStores];
      state.storeListTotal = action.payload.total;
      state.storeListHasMore = action.payload.hasMore;
      state.storeListPage += 1;
      state.error = "";
    });
    builder.addCase(loadMoreStores.rejected, (state, action) => {
      state.storeListLoadingMore = false;
      state.error = action.payload?.message || "Failed to load more stores";
    });

    // fetchSuperadminById
    builder.addCase(fetchSuperadminById.pending, (state) => {
      state.superadminDetailLoading = true;
    });
    builder.addCase(fetchSuperadminById.fulfilled, (state, action) => {
      state.superadminDetailLoading = false;
      state.superadminDetail = action.payload;
      state.error = "";
    });
    builder.addCase(fetchSuperadminById.rejected, (state, action) => {
      state.superadminDetailLoading = false;
      state.superadminDetail = null;
      state.error = action.payload?.message || "Failed to fetch superadmin";
    });

    // addSuperadminPermission
    builder.addCase(addSuperadminPermission.pending, (state) => {
      state.permissionSubmitLoading = true;
    });
    builder.addCase(addSuperadminPermission.fulfilled, (state, action) => {
      state.permissionSubmitLoading = false;
      state.successMessage = action.payload.msg;
      state.error = "";
    });
    builder.addCase(addSuperadminPermission.rejected, (state, action) => {
      state.permissionSubmitLoading = false;
      state.error = action.payload?.message || action.payload || "Failed to update permissions";
    });

    // insertSuperadmin
    builder.addCase(insertSuperadmin.pending, (state) => {
      state.superadminSubmitLoading = true;
    });
    builder.addCase(insertSuperadmin.fulfilled, (state, action) => {
      state.superadminSubmitLoading = false;
      state.successMessage = action.payload.msg;
      state.error = "";
    });
    builder.addCase(insertSuperadmin.rejected, (state, action) => {
      state.superadminSubmitLoading = false;
      state.error = action.payload?.message || action.payload || "Failed to insert superadmin";
    });

    // updateSuperadmin
    builder.addCase(updateSuperadmin.pending, (state) => {
      state.superadminSubmitLoading = true;
    });
    builder.addCase(updateSuperadmin.fulfilled, (state, action) => {
      state.superadminSubmitLoading = false;
      state.successMessage = action.payload.msg;
      state.error = "";
    });
    builder.addCase(updateSuperadmin.rejected, (state, action) => {
      state.superadminSubmitLoading = false;
      state.error = action.payload?.message || action.payload || "Failed to update superadmin";
    });
  },
});

// Export actions
export const { clearAdminPermissions, clearSuperadminDetail, clearStoreList } = superadminSlice.actions;

export default superadminSlice.reducer;
